/**
 * Onglet **Musculation** — le 5ᵉ (`ARCHITECTURE_V2.md` §2.5 et §4.6, specs §8.2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 DEUX SOUS-ONGLETS : ROUTINES ET EXERCICES
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Décision prise **après usage réel sur l'appareil**, le 28/08/2026, et elle
 * revient sur deux documents à la fois : le §4.6 rangeait les exercices dans un
 * quatrième sous-onglet de la **Bibliothèque**, et le §8.2 des specs faisait de
 * même (« Bibliothèque : + Exercices, routines »).
 *
 * Le raisonnement documentaire n'était pas faux — un exercice *est* un objet de
 * référentiel, comme un aliment ou une recette. Il ratait seulement ce qui
 * décide de l'emplacement, et que le §4.6 énonce lui-même deux lignes plus
 * bas : « l'objet va là où se trouve le geste qui l'emploie ». On ouvre un
 * exercice **en composant une routine**, ou **en salle** — jamais en même temps
 * qu'on corrige les macros d'un yaourt. La Bibliothèque redevient donc
 * l'onglet de la nutrition (modèles, aliments, recettes), et le domaine
 * musculation cesse d'être coupé en deux.
 *
 * ⚠️ **Deux noms, et ce n'est pas une inconséquence** : la barre affiche
 * « Muscu », ce titre affiche « Musculation ». La contrainte de largeur est
 * celle de la barre (une boîte de 59 px à 375), pas celle d'un titre pleine
 * largeur — abréger là où la place ne manque pas serait gratuit. C'est la
 * pratique d'iOS, et l'historique complet du libellé est dans `TabBar.tsx`.
 *
 * ⚠️ **Le sommaire est celui de la Bibliothèque**, pas celui des Réglages :
 * `SectionTabs` (des liens, donc la section est **dans l'URL**), et deux
 * libellés tiennent sans peine dans la rangée. Ce qui donne gratuitement la
 * mémoire d'onglet, le retour par glissement natif et le montage paresseux de
 * chaque section.
 */

import { useParams } from 'react-router';

import { ScreenTitle } from '../../components/ui/ScreenHeader';
import { SectionTabs, type SectionTab } from '../../components/ui/SectionTabs';
import { ExercisesSection } from './ExercisesSection';
import { RoutinesSection } from './RoutinesSection';
import { SessionsSection } from './SessionsSection';

/**
 * ⚠️ **Les routines d'abord.** C'est ce qu'on vient chercher : on ouvre cet
 * onglet pour faire une séance, pas pour lire une fiche d'exercice. Les
 * exercices sont le référentiel qui la sert — même rapport qu'entre les
 * recettes et les aliments dans la Bibliothèque, où les aliments ne sont pas
 * non plus en tête.
 */
const TABS: readonly SectionTab[] = [
  // `null` = la section par défaut, servie par `/musculation` tout court.
  { section: null, label: 'Routines' },
  /*
   * ⚠️ **L'historique en 2ᵉ, avant les exercices** *(étape 6)*. L'ordre suit
   * la fréquence du geste : on ouvre cet onglet pour faire une séance, puis
   * pour relire celles d'avant ; les exercices sont le référentiel qui sert
   * les deux, et c'est le plus rarement consulté — même rapport qu'entre les
   * recettes et les aliments dans la Bibliothèque.
   */
  { section: 'seances', label: 'Séances' },
  { section: 'exercices', label: 'Exercices' },
];

export function MusculationScreen() {
  /*
   * ⚠️ **Deux paramètres, jamais les deux à la fois.** `section` vient de
   * `/musculation/:section`, `clientId` de `/musculation/routine/:clientId` —
   * deux routes distinctes déclarées dans `App.tsx`. Une routine ouverte est
   * donc toujours dans la section par défaut, ce qui allume le bon sous-onglet
   * sans avoir à le dire.
   */
  const { section, clientId } = useParams<{ section?: string; clientId?: string }>();

  /*
   * ⚠️ Une section inconnue retombe sur celle par défaut plutôt que d'afficher
   * une page vide : `/musculation/nimportequoi` reste une adresse qui montre
   * quelque chose. Même normalisation que la Bibliothèque.
   *
   * ⚠️ **`seance` (singulier) n'est PAS une section** — c'est le préfixe de la
   * séance en direct, `/musculation/seance/:clientId`, déclarée avant cette
   * route dans `App.tsx`. Elle ne peut donc pas être captée ici, et si elle
   * l'était elle retomberait sur les routines plutôt que d'afficher un vide.
   */
  const courante = section === 'exercices' || section === 'seances' ? section : null;

  return (
    <section className="n-page">
      <ScreenTitle>Musculation</ScreenTitle>

      <SectionTabs
        base="/musculation"
        tabs={TABS}
        current={courante}
        ariaLabel="Sections de la musculation"
      />

      {courante === null && <RoutinesSection openedClientId={clientId ?? null} />}
      {courante === 'seances' && <SessionsSection />}
      {courante === 'exercices' && <ExercisesSection />}
    </section>
  );
}
